import React from 'react';
import {
  BiChevronLeft,
  BiChevronsLeft,
  BiChevronRight,
  BiChevronsRight,
  BiDotsHorizontalRounded,
} from 'react-icons/bi';

export interface PaginationProps {
  total: number;
  current: number;
  onChange: (page: number) => void;
}

interface PaginationButtonProps {
  onClick: () => void;
  disabled?: boolean;
  isSelected?: boolean;
  children: React.ReactNode;
}

const PaginationButton: React.FC<PaginationButtonProps> = ({
  onClick,
  disabled = false,
  isSelected = false,
  children,
}) => {
  const buttonCommonClass =
    'mx-0 lg:mx-1 flex h-6 w-6 items-center justify-center focus:outline-none rounded-corner-pill';
  const enabledButtonClass = `${buttonCommonClass} hover:bg-gray-5 cursor-pointer`;
  const disabledButtonClass = `${buttonCommonClass} cursor-not-allowed disabled:text-gray-20`;
  const selectedButtonClass = `${buttonCommonClass} bg-blue-5 text-blue-80 cursor-pointer`;
  const pressedButtonClass = 'bg-gray-30';

  // イベントハンドラ: ボタンがマウスまたはタッチで押された瞬間にスタイルを適用
  const handlePress = (
    e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>,
  ) => {
    const target = e.currentTarget;
    target.classList.add(pressedButtonClass);
  };

  // イベントハンドラ: ボタンがマウスまたはタッチで離された瞬間にスタイルを元に戻す
  const handleRelease = (
    e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>,
  ) => {
    const target = e.currentTarget;
    target.classList.remove(pressedButtonClass);
  };

  const buttonClass = disabled
    ? disabledButtonClass
    : isSelected
    ? selectedButtonClass
    : enabledButtonClass;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseDown={handlePress}
      onMouseUp={handleRelease}
      onTouchStart={handlePress}
      onTouchEnd={handleRelease}
      className={buttonClass}
    >
      {children}
    </button>
  );
};

export function Pagination({ total, current, onChange }: PaginationProps) {
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const startPoint = current <= 3 ? 1 : current >= total - 2 ? total - 4 : current - 2;
    const endPoint = current <= 3 ? 5 : current >= total - 2 ? total : current + 2;

    if (startPoint > 1) {
      pageNumbers.push(
        <PaginationButton key={1} onClick={() => onChange(1)}>
          1
        </PaginationButton>,
      );
      if (startPoint > 2) {
        pageNumbers.push(<BiDotsHorizontalRounded key='left-dots' className='mx-1 h-6 w-6' />);
      }
    }

    for (let i = startPoint; i <= endPoint; i++) {
      if (i > 0 && i <= total) {
        pageNumbers.push(
          <PaginationButton key={i} onClick={() => onChange(i)} isSelected={current === i}>
            {i}
          </PaginationButton>,
        );
      }
    }

    if (endPoint < total) {
      if (endPoint < total - 1) {
        pageNumbers.push(<BiDotsHorizontalRounded key='right-dots' className='mx-1 h-6 w-6' />);
      }
      pageNumbers.push(
        <PaginationButton key={total} onClick={() => onChange(total)}>
          {total}
        </PaginationButton>,
      );
    }

    return pageNumbers;
  };

  return (
    <div className='my-1.5 flex items-center justify-center'>
      {/* 最初のページに移動するボタン */}
      <PaginationButton onClick={() => onChange(1)} disabled={current === 1}>
        <BiChevronsLeft className='h-full w-full' />
      </PaginationButton>

      {/* 前のページに移動するボタン */}
      <PaginationButton onClick={() => onChange(current - 1)} disabled={current === 1}>
        <BiChevronLeft className='h-full w-full' />
      </PaginationButton>

      {renderPageNumbers()}

      {/* 次のページに移動するボタン */}
      <PaginationButton
        onClick={() => onChange(Math.min(total, current + 1))}
        disabled={current === total}
      >
        <BiChevronRight className='h-full w-full' />
      </PaginationButton>

      {/* 最後のページに移動するボタン */}
      <PaginationButton onClick={() => onChange(total)} disabled={current === total}>
        <BiChevronsRight className='h-full w-full' />
      </PaginationButton>
    </div>
  );
}
